'use client';

import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';

import { Capabilities } from '@kit/capabilities';
import { useCapabilityOptional } from '@kit/capabilities/client';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { SectionHeader } from '~/(tenant)/_components/native/_primitives';

import { RotatingQrCard } from './_member-id-card/rotating-qr-card';

interface MemberIdCardSectionProps {
  accountId: string;
}

export type MembershipStatus =
  | 'active'
  // 'approved' = admin approved a paid-plan applicant, customer hasn't paid yet.
  // Member-side renders this similarly to 'pending' (no usable QR, "awaiting payment"
  // hint). The activate_due_memberships cron + payment trigger flip this to 'active'.
  | 'approved'
  | 'pending'
  | 'expired'
  | 'suspended'
  | 'on_hold'
  | 'cancelled';

interface MembershipForCard {
  id: string;
  qr_secret: string | null;
  member_number: string | null;
  status: MembershipStatus;
  plan_name: string;
  member_name: string;
}

interface UserMembershipRow {
  id: string;
  qr_secret: string | null;
  member_number: string | null;
  status: string;
  membership_plans: { name: string | null } | null;
}

interface AccountRow {
  name: string | null;
}

type SupabaseLike = {
  from: (table: string) => UntypedQuery;
};
type UntypedQuery = {
  select: (columns: string) => UntypedQuery;
  eq: (col: string, val: unknown) => UntypedQuery;
  is: (col: string, val: unknown) => UntypedQuery;
  in: (col: string, vals: readonly unknown[]) => UntypedQuery;
  order: (col: string, opts: { ascending: boolean }) => UntypedQuery;
  limit: (n: number) => UntypedQuery;
  maybeSingle: <T = unknown>() => Promise<{ data: T | null }>;
};

/**
 * Member ID card section. Hidden when:
 *   - tenant has no MEMBER_QR_CHECKIN capability
 *   - user is not signed in
 *   - user has no membership row at all for this tenant
 *   - the most recent membership is `cancelled` (treated as no membership)
 *
 * Per the Digital Member ID PRD, the card must render for non-active statuses
 * (pending payment, expired, suspended, on_hold) so the member understands
 * *why* their QR is gone. We fetch the most recent non-deleted membership
 * regardless of status and let the child component decide the visual.
 *
 * The membership's `qr_secret` is fetched once and passed down to the client
 * child for live HMAC rotation when `status === 'active'`. Per plan §6a.1, the
 * secret is meant to live on the device — gate WiFi is unreliable, so the
 * rotating QR has to be derivable offline.
 */
export function MemberIdCardSection({ accountId }: MemberIdCardSectionProps) {
  const enabled = useCapabilityOptional(Capabilities.MEMBER_QR_CHECKIN);
  const client = useSupabase();

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
    enabled,
  });

  const userId = session?.user?.id;

  const { data: membership } = useQuery({
    queryKey: ['member-id-card', accountId, userId],
    enabled: enabled && Boolean(userId),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<MembershipForCard | null> => {
      if (!userId) return null;

      // TODO(member-club-phase-1): cast to SupabaseLike until the
      // 51-guest-passes.sql migration runs and types regenerate. Once
      // `qr_secret` and `member_number` exist on the generated
      // `user_memberships` row, swap to the typed `client.from(...)`.
      const c = client as unknown as SupabaseLike;

      const [membershipResult, accountResult] = await Promise.all([
        c
          .from('user_memberships')
          .select(
            'id, qr_secret, member_number, status, membership_plans:plan_id(name)',
          )
          .eq('user_id', userId)
          .eq('account_id', accountId)
          .eq('is_deleted', false)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle<UserMembershipRow>(),
        client
          .from('accounts')
          .select('name')
          .eq('id', userId)
          .maybeSingle<AccountRow>(),
      ]);

      const row = membershipResult.data;
      if (!row) return null;

      const planName = row.membership_plans?.name ?? '';
      const memberName = (accountResult.data?.name ?? '').trim() || '';

      return {
        id: row.id,
        qr_secret: row.qr_secret,
        member_number: row.member_number,
        status: row.status as MembershipStatus,
        plan_name: planName,
        member_name: memberName,
      };
    },
  });

  if (!enabled) return null;
  if (!userId || !membership) return null;
  // Cancelled memberships are equivalent to no membership for the member-facing
  // card; the child returns null but we short-circuit here so we don't render
  // an empty section header.
  if (membership.status === 'cancelled') return null;

  return <MemberIdCardLayout membership={membership} />;
}

function MemberIdCardLayout({
  membership,
}: {
  membership: MembershipForCard;
}) {
  const t = useNativeTokens();

  return (
    <section style={{ paddingTop: 32 }}>
      <SectionHeader
        eyebrow={
          <Trans
            i18nKey="common:nativeHome.memberIdCard.eyebrow"
            defaults="Members only"
          />
        }
        title={
          <Trans
            i18nKey="common:nativeHome.memberIdCard.title"
            defaults="Your member ID"
          />
        }
      />
      <div style={{ marginTop: 18, padding: '0 22px' }}>
        <RotatingQrCard
          membershipId={membership.id}
          qrSecret={membership.qr_secret}
          status={membership.status}
          memberName={
            membership.member_name ||
            // Fall back to member number when the account name is blank
            // (e.g. WhatsApp guest contacts who never set a display name).
            membership.member_number ||
            ''
          }
          planName={membership.plan_name}
          memberNumber={membership.member_number ?? ''}
        />
        {membership.status === 'active' ? (
          <div
            style={{
              marginTop: 10,
              fontFamily: t.fontMono,
              fontSize: 8,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: t.muted,
            }}
          >
            <Trans
              i18nKey="common:nativeHome.memberIdCard.helper"
              defaults="Show at the gate · Refreshes every 30s"
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}
